import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Badge,
  Modal,
  Table,
} from "react-bootstrap";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import axios from "axios";

import {
  FaCubes,
  FaUsers,
} from "react-icons/fa";


const SalesChartBrand = () => {
  /* ============================================================
     STATE
  ============================================================ */

  const [brandData, setBrandData] = useState([]);

  // Tutte le righe cliente/brand prese da
  // mancata_vendita_dashboard.json
  const [clientiBrandData, setClientiBrandData] = useState([]);

  const [showBrandModal, setShowBrandModal] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= 767
      : false
  );


  /* ============================================================
     NORMALIZZAZIONE BRAND

     Serve per evitare problemi tipo:

     "FIAT  GROUP"
     "FIAT GROUP"
     " fiat group "

     che devono essere considerati lo stesso brand.
  ============================================================ */

  const normalizeBrand = (value) =>
    String(value || "")
      .trim()
      .replace(/\s+/g, " ")
      .toUpperCase();


  /* ============================================================
     FETCH DATI
  ============================================================ */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          dashboardRes,
          clientiRes,
        ] = await Promise.all([
          axios.get("./dati_dashboard.json"),

          axios.get("./mancata_vendita_dashboard.json"),
        ]);


        /* --------------------------------------------------------
           DATI GRAFICO BRAND
        -------------------------------------------------------- */

        const orarioBrandData =
          dashboardRes.data?.AR?.orario_brand || [];

        setBrandData(
          prepareDataForChart(orarioBrandData)
        );


        /* --------------------------------------------------------
           DATI CLIENTI / BRAND

           Nel tuo JSON questi dati sono dentro:

           AR.localita

           Ogni riga contiene già:
           - id_cliente
           - clienti
           - descrizione
           - Valore
           - numero_doc
           - regione
           - nazione
           - origine
        -------------------------------------------------------- */

        const clienti =
          clientiRes.data?.AR?.localita || [];

        setClientiBrandData(clienti);

      } catch (err) {
        console.error(
          "Errore caricamento Performance Brand:",
          err
        );
      }
    };


    fetchData();
  }, []);


  /* ============================================================
     RESIZE
  ============================================================ */

  useEffect(() => {
    const handleResize = () =>
      setIsMobile(window.innerWidth <= 767);

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );

  }, []);


  /* ============================================================
     PREPARA DATI GRAFICO
  ============================================================ */

  const prepareDataForChart = (
    orarioBrandData
  ) => {
    const brandSales = {};


    orarioBrandData.forEach((item) => {
      const {
        descrizione,
        valore,
        numero,
      } = item;


      const brand =
        descrizione || "Generico";


      if (!brandSales[brand]) {
        brandSales[brand] = {
          value: 0,
          numero: 0,
        };
      }


      brandSales[brand].value +=
        parseFloat(valore || 0);


      brandSales[brand].numero +=
        Number(numero || 0);
    });


    return Object.entries(
      brandSales
    )
      .map(
        ([
          brand,
          values,
        ]) => ({
          brand,
          value: values.value,
          numero: values.numero,
        })
      )
      .sort(
        (a, b) =>
          b.value - a.value
      );
  };


  /* ============================================================
     CLIENTI DEL BRAND SELEZIONATO
  ============================================================ */

  const selectedBrandClients = useMemo(() => {

    if (!selectedBrand) {
      return [];
    }


    const selected =
      normalizeBrand(
        selectedBrand.brand
      );


    const grouped = {};


    clientiBrandData.forEach(
      (item) => {

        const itemBrand =
          normalizeBrand(
            item.descrizione
          );


        // La riga appartiene ad un altro brand
        if (
          itemBrand !== selected
        ) {
          return;
        }


        const id =
          item.id_cliente ??
          item.codice_cliente ??
          "";


        const origine =
          item.origine || "noweb";


        /*
         * Manteniamo separati WEB e NOWEB
         * come fai già nella tabella clienti.
         */
        const key =
          `${id}-${origine}`;


        const valore =
          parseFloat(
            String(
              item.Valore ??
              item.valore ??
              0
            ).replace(",", ".")
          ) || 0;


        const numero =
          Number(
            item.numero_doc ??
            item.numero ??
            0
          );


        if (!grouped[key]) {

          grouped[key] = {
            id_cliente: id,

            cliente:
              item.clienti ??
              item.cliente ??
              `Cliente ${id}`,

            localita:
              item.regione ??
              item.localita ??
              item.nazione ??
              item.paese ??
              "-",

            origine,

            valore: 0,

            numero: 0,
          };
        }


        grouped[key].valore +=
          valore;


        grouped[key].numero +=
          numero;
      }
    );


    return Object.values(grouped)
      .sort(
        (a, b) =>
          b.valore - a.valore
      );

  }, [
    selectedBrand,
    clientiBrandData,
  ]);


  /* ============================================================
     TOTALI MODALE
  ============================================================ */

  const modalTotals = useMemo(() => {

    return selectedBrandClients.reduce(
      (acc, cliente) => {

        acc.valore +=
          Number(
            cliente.valore || 0
          );


        acc.numero +=
          Number(
            cliente.numero || 0
          );


        return acc;

      },
      {
        valore: 0,
        numero: 0,
      }
    );

  }, [selectedBrandClients]);


  /* ============================================================
     CLICK BRAND
  ============================================================ */

  const handleBrandClick = (
    brand
  ) => {

    setSelectedBrand(brand);

    setShowBrandModal(true);
  };


  /* ============================================================
     FORMAT EURO
  ============================================================ */

  const formatEuro = (
    val
  ) =>
    new Intl.NumberFormat(
      "it-IT",
      {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }
    ).format(
      Number(val || 0)
    );


  /* ============================================================
     DATI DA MOSTRARE
  ============================================================ */

  const chartData =
    isMobile
      ? brandData.slice(
          0,
          4
        )
      : brandData;


  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      {/* ========================================================
          GRAFICO
      ======================================================== */}

      <Card
        className="
          card
          bg-dark
          ombra
          card-week
          card-home
          hover-effect
          border-0
          am-chart-card
        "
        style={{
          borderRadius:
            "15px",
        }}
      >

        {/* HEADER */}

        <div
          className="
            d-flex
            justify-content-between
            align-items-center
            px-3
            am-chart-header
          "
        >

          <h6
            className="
              text-title2
              fw-bold
              mb-0
            "
            style={{
              letterSpacing:
                "1px",
            }}
          >
            PERFORMANCE BRAND
          </h6>


          <Badge
            className="
              am-badge-orange
            "
            style={{
              fontSize:
                "0.6rem",
            }}
          >
            TOP SALES
          </Badge>

        </div>


        {/* GRAFICO */}

        <div className="am-chart-body">

          <ResponsiveContainer
            width="100%"
            height="100%"
          >

            <BarChart
              data={chartData}
              margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom:
                  isMobile
                    ? 10
                    : 20,
              }}
              barSize={
                isMobile
                  ? 48
                  : 35
              }
            >

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="rgba(218,218,218,0.06)"
              />


              <XAxis
                dataKey="brand"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill:
                    "#9ca0a8",

                  fontSize:
                    isMobile
                      ? 11
                      : 10,

                  fontWeight:
                    700,
                }}
                interval={0}
                angle={
                  isMobile
                    ? 0
                    : -15
                }
                textAnchor={
                  isMobile
                    ? "middle"
                    : "end"
                }
                tickFormatter={(
                  value
                ) => {

                  const brand =
                    String(value);

                  return (
                    isMobile &&
                    brand.length >
                      10
                  )
                    ? `${brand.substring(
                        0,
                        10
                      )}…`
                    : brand;
                }}
              />


              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{
                  fill:
                    "#9ca0a8",
                  fontSize:
                    10,
                }}
                tickFormatter={
                  formatEuro
                }
              />


              <Tooltip
                cursor={{
                  fill:
                    "rgba(255,255,255,0.025)",
                }}
                contentStyle={{
                  borderRadius:
                    "12px",

                  border:
                    "1px solid rgba(255,255,255,0.12)",

                  background:
                    "#25282f",

                  color:
                    "#fff",

                  boxShadow:
                    "0 10px 30px rgba(0,0,0,0.28)",

                  fontSize:
                    "12px",
                }}
                labelStyle={{
                  color:
                    "#fff",

                  fontWeight:
                    "700",

                  marginBottom:
                    "4px",
                }}
                itemStyle={{
                  color:
                    "#f16f24",

                  fontWeight:
                    "800",
                }}
                formatter={(
                  value
                ) => [
                  formatEuro(
                    value
                  ),
                  "Totale Ordinato",
                ]}
              />


              {/* =================================================
                  BARRE CLICCABILI
              ================================================= */}

              <Bar
                dataKey="value"
                radius={[
                  6,
                  6,
                  0,
                  0,
                ]}
              >

                {chartData.map(
                  (
                    entry,
                    index
                  ) => (

                    <Cell
                      key={`cell-${index}`}
                      fill={
                        index === 0
                          ? "#f16f24"
                          : "#5a5a5a"
                      }
                      onClick={() =>
                        handleBrandClick(
                          entry
                        )
                      }
                      style={{
                        transition:
                          "fill 0.3s ease",

                        cursor:
                          "pointer",
                      }}
                    />

                  )
                )}

              </Bar>

            </BarChart>

          </ResponsiveContainer>

        </div>

      </Card>


      {/* ========================================================
          MODALE BRAND
      ======================================================== */}

      <Modal
        show={
          showBrandModal
        }
        onHide={() =>
          setShowBrandModal(
            false
          )
        }
        centered
        size="lg"
        className="am-brand-modal"
        dialogClassName="am-brand-modal-dialog"
        contentClassName="am-brand-modal-content"
        backdropClassName="am-brand-modal-backdrop"
      >

        {/* HEADER */}

        <Modal.Header
          closeButton
          className="
            am-brand-modal-header
            border-0
          "
        >

          <Modal.Title
            className="
              d-flex
              align-items-center
              w-100
            "
          >

            <div className="am-brand-modal-icon">

              <FaCubes
                size={20}
              />

            </div>


            <div className="am-brand-modal-heading">

              <div className="am-brand-modal-name">
                {
                  selectedBrand?.brand
                }
              </div>


              <div className="am-brand-modal-subtitle">

                <FaUsers />

                <span>
                  {
                    selectedBrandClients.length
                  }{" "}
                  clienti
                </span>

              </div>

            </div>

          </Modal.Title>

        </Modal.Header>


        {/* BODY */}

        <Modal.Body className="am-brand-modal-body">

          {/* BADGE */}

          <div className="am-brand-modal-meta">

            <Badge
              pill
              className="am-brand-badge"
            >
              BRAND
            </Badge>


            <Badge
              pill
              className="am-brand-count-badge"
            >
              {
                modalTotals.numero
              }{" "}
              ORDINI
            </Badge>

          </div>


          {/* =====================================================
              KPI
          ===================================================== */}

          <div className="am-brand-modal-kpis">

            <div className="am-brand-kpi">

              <span>
                VALORE BRAND
              </span>

              <strong>
                {formatEuro(
                  selectedBrand?.value
                )}
              </strong>

            </div>


            <div className="am-brand-kpi">

              <span>
                CLIENTI
              </span>

              <strong>
                {
                  selectedBrandClients.length
                }
              </strong>

            </div>


            <div className="am-brand-kpi">

              <span>
                ORDINI
              </span>

              <strong>
                {
                  modalTotals.numero
                }
              </strong>

            </div>

          </div>


          {/* =====================================================
              LISTA CLIENTI
          ===================================================== */}

          <div className="am-brand-detail-panel">

            <div className="am-brand-detail-header">

              <div>

                <small>
                  DETTAGLIO BRAND
                </small>

                <strong>
                  Clienti che hanno ordinato
                </strong>

              </div>


              <span>
                {
                  selectedBrandClients.length
                }{" "}
                clienti
              </span>

            </div>


            <div className="am-brand-detail-scroll">

              {selectedBrandClients.length >
              0 ? (

                <Table
                  hover
                  className="
                    mb-0
                    am-brand-detail-table
                  "
                >

                  <thead>

                    <tr>

                      <th>
                        CLIENTE
                      </th>

                      <th className="am-brand-location-column">
                        LOCALITÀ
                      </th>

                      <th className="text-center">
                        ORDINI
                      </th>

                      <th className="text-center am-brand-origin-column">
                        ORIGINE
                      </th>

                      <th className="text-end">
                        VALORE
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {selectedBrandClients.map(
                      (
                        cliente,
                        index
                      ) => (

                        <tr
                          key={`${cliente.id_cliente}-${cliente.origine}-${index}`}
                        >

                          <td>

                            <div className="am-brand-client-name">

                              {
                                cliente.cliente
                              }

                            </div>

                            <small className="am-brand-client-id">

                              ID{" "}
                              {
                                cliente.id_cliente
                              }

                            </small>

                          </td>


                          <td className="am-brand-location-column">

                            {
                              cliente.localita
                            }

                          </td>


                          <td className="text-center">

                            {
                              cliente.numero
                            }

                          </td>


                          <td className="text-center am-brand-origin-column">

                            <Badge
                              pill
                              className="am-brand-origin-badge"
                            >

                              {
                                cliente.origine
                                  ?.toUpperCase()
                              }

                            </Badge>

                          </td>


                          <td className="text-end">

                            <strong className="am-brand-detail-value">

                              {formatEuro(
                                cliente.valore
                              )}

                            </strong>

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>


                  <tfoot>

                    <tr>

                      <td
                        colSpan={
                          isMobile
                            ? 2
                            : 4
                        }
                      >
                        TOTALE
                      </td>


                      <td className="text-end">

                        {formatEuro(
                          modalTotals.valore
                        )}

                      </td>

                    </tr>

                  </tfoot>

                </Table>

              ) : (

                <div className="am-brand-empty">

                  <FaUsers
                    size={28}
                  />

                  <strong>
                    Nessun cliente trovato
                  </strong>

                  <span>
                    Non risultano dettagli cliente per questo brand.
                  </span>

                </div>

              )}

            </div>

          </div>

        </Modal.Body>

      </Modal>


      {/* ========================================================
          CSS MODALE
      ======================================================== */}

      <style>{`

        /* ======================================================
           BACKDROP
        ====================================================== */

        .am-brand-modal-backdrop.show {
          opacity: 0.88 !important;
          background: #07090d !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }


        /* ======================================================
           DIALOG
        ====================================================== */

        .am-brand-modal-dialog {
          width: min(
            900px,
            calc(100vw - 32px)
          ) !important;

          max-width: 900px !important;

          margin:
            16px auto !important;
        }


        .am-brand-modal-content {
          position:
            relative;

          max-height:
            calc(
              100dvh - 32px
            );

          overflow:
            hidden;

          color:
            #f5f6f8 !important;

          background:

            radial-gradient(
              circle at 0% 0%,
              rgba(
                241,
                111,
                36,
                0.18
              ),
              transparent 34%
            ),

            radial-gradient(
              circle at 100% 8%,
              rgba(
                255,
                255,
                255,
                0.045
              ),
              transparent 28%
            ),

            linear-gradient(
              145deg,
              rgba(
                39,
                42,
                49,
                0.94
              ),
              rgba(
                20,
                22,
                27,
                0.97
              )
            ) !important;

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.11
            ) !important;

          border-radius:
            24px !important;

          box-shadow:

            0 32px 80px
            rgba(
              0,
              0,
              0,
              0.58
            ),

            inset
            0 1px 0
            rgba(
              255,
              255,
              255,
              0.09
            ) !important;

          backdrop-filter:
            blur(8px)
            saturate(115%);

          -webkit-backdrop-filter:
            blur(8px)
            saturate(115%);
        }


        .am-brand-modal-content::before {
          content:
            "";

          position:
            absolute;

          top:
            0;

          left:
            24px;

          width:
            42%;

          height:
            1px;

          background:
            linear-gradient(
              90deg,
              #f16f24,
              transparent
            );

          pointer-events:
            none;

          z-index:
            3;
        }


        /* ======================================================
           HEADER
        ====================================================== */

        .am-brand-modal-header {
          padding:
            20px
            22px
            13px !important;

          background:
            transparent !important;

          border-bottom:
            1px solid
            rgba(
              255,
              255,
              255,
              0.06
            ) !important;
        }


        .am-brand-modal-icon {
          flex:
            0 0 44px;

          width:
            44px;

          height:
            44px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          margin-right:
            12px;

          color:
            #ff8847;

          background:
            linear-gradient(
              145deg,
              rgba(
                241,
                111,
                36,
                0.22
              ),
              rgba(
                241,
                111,
                36,
                0.08
              )
            );

          border:
            1px solid
            rgba(
              241,
              111,
              36,
              0.30
            );

          border-radius:
            14px;

          box-shadow:
            inset
            0 1px 0
            rgba(
              255,
              255,
              255,
              0.055
            );
        }


        .am-brand-modal-heading {
          min-width:
            0;
        }


        .am-brand-modal-name {
          max-width:
            100%;

          overflow:
            hidden;

          color:
            #fff;

          font-size:
            1.15rem;

          font-weight:
            850;

          line-height:
            1.15;

          text-overflow:
            ellipsis;

          white-space:
            nowrap;
        }


        .am-brand-modal-subtitle {
          display:
            flex;

          align-items:
            center;

          gap:
            5px;

          margin-top:
            5px;

          color:
            rgba(
              255,
              255,
              255,
              0.48
            );

          font-size:
            0.72rem;

          font-weight:
            650;
        }


        .am-brand-modal-subtitle svg {
          color:
            #f16f24;
        }


        .am-brand-modal-content
        .btn-close {
          width:
            34px;

          height:
            34px;

          margin:
            0 !important;

          padding:
            0 !important;

          border-radius:
            50%;

          background-color:
            rgba(
              255,
              255,
              255,
              0.075
            );

          background-size:
            11px;

          filter:
            invert(1)
            grayscale(1);

          opacity:
            0.75;
        }


        .am-brand-modal-content
        .btn-close:hover {
          opacity:
            1;

          background-color:
            rgba(
              241,
              111,
              36,
              0.17
            );
        }


        /* ======================================================
           BODY
        ====================================================== */

        .am-brand-modal-body {
          padding:
            10px
            22px
            22px !important;

          overflow-y:
            auto;

          background:
            transparent !important;

          scrollbar-width:
            none !important;
        }


        .am-brand-modal-body::-webkit-scrollbar {
          display:
            none;
        }


        /* ======================================================
           META
        ====================================================== */

        .am-brand-modal-meta {
          display:
            flex;

          flex-wrap:
            wrap;

          gap:
            7px;

          margin-bottom:
            12px;
        }


        .am-brand-badge,
        .am-brand-count-badge {
          padding:
            5px
            9px !important;

          font-size:
            0.60rem !important;

          font-weight:
            800 !important;

          letter-spacing:
            0.04em;
        }


        .am-brand-badge {
          color:
            #ff9a60 !important;

          background:
            rgba(
              241,
              111,
              36,
              0.12
            ) !important;

          border:
            1px solid
            rgba(
              241,
              111,
              36,
              0.32
            ) !important;
        }


        .am-brand-count-badge {
          color:
            rgba(
              255,
              255,
              255,
              0.62
            ) !important;

          background:
            rgba(
              255,
              255,
              255,
              0.055
            ) !important;

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.085
            ) !important;
        }


        /* ======================================================
           KPI
        ====================================================== */

        .am-brand-modal-kpis {
          display:
            grid;

          grid-template-columns:
            2fr 1fr 1fr;

          gap:
            10px;

          margin-bottom:
            12px;
        }


        .am-brand-kpi {
          padding:
            12px
            14px;

          background:
            linear-gradient(
              145deg,
              rgba(
                255,
                255,
                255,
                0.055
              ),
              rgba(
                255,
                255,
                255,
                0.025
              )
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.075
            );

          border-radius:
            14px;

          box-shadow:
            inset
            0 1px 0
            rgba(
              255,
              255,
              255,
              0.035
            );
        }


        .am-brand-kpi span {
          display:
            block;

          margin-bottom:
            4px;

          color:
            rgba(
              255,
              255,
              255,
              0.36
            );

          font-size:
            0.56rem;

          font-weight:
            850;

          letter-spacing:
            0.075em;
        }


        .am-brand-kpi strong {
          display:
            block;

          color:
            #fff;

          font-size:
            1rem;

          font-weight:
            850;
        }


        /* ======================================================
           PANEL
        ====================================================== */

        .am-brand-detail-panel {
          overflow:
            hidden;

          background:
            rgba(
              8,
              10,
              13,
              0.24
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.075
            );

          border-radius:
            16px;
        }


        .am-brand-detail-header {
          display:
            flex;

          align-items:
            center;

          justify-content:
            space-between;

          gap:
            12px;

          padding:
            11px
            13px;

          border-bottom:
            1px solid
            rgba(
              255,
              255,
              255,
              0.06
            );
        }


        .am-brand-detail-header > div {
          display:
            flex;

          flex-direction:
            column;
        }


        .am-brand-detail-header small {
          color:
            rgba(
              255,
              255,
              255,
              0.34
            );

          font-size:
            0.53rem;

          font-weight:
            850;

          letter-spacing:
            0.08em;
        }


        .am-brand-detail-header strong {
          margin-top:
            2px;

          color:
            rgba(
              255,
              255,
              255,
              0.86
            );

          font-size:
            0.76rem;
        }


        .am-brand-detail-header > span {
          color:
            rgba(
              255,
              255,
              255,
              0.40
            );

          font-size:
            0.63rem;

          font-weight:
            700;
        }


        /* ======================================================
           SCROLL
        ====================================================== */

        .am-brand-detail-scroll {
          max-height:
            390px;

          overflow-y:
            auto;

          overflow-x:
            auto;

          scrollbar-width:
            none;

          overscroll-behavior:
            contain;
        }


        .am-brand-detail-scroll::-webkit-scrollbar {
          display:
            none;
        }


        /* ======================================================
           TABLE
        ====================================================== */

        .am-brand-detail-table {
          width:
            100%;

          margin:
            0 !important;

          color:
            rgba(
              255,
              255,
              255,
              0.70
            ) !important;

          background:
            transparent !important;
        }


        .am-brand-detail-table
        > :not(caption)
        > *
        > * {
          background:
            transparent !important;

          box-shadow:
            none !important;
        }


        .am-brand-detail-table
        thead
        th {
          position:
            sticky;

          top:
            0;

          z-index:
            3;

          padding:
            10px
            12px !important;

          color:
            rgba(
              255,
              255,
              255,
              0.38
            ) !important;

          background:
            #191c21 !important;

          border:
            0 !important;

          border-bottom:
            1px solid
            rgba(
              255,
              255,
              255,
              0.065
            ) !important;

          font-size:
            0.57rem;

          font-weight:
            850;

          letter-spacing:
            0.075em;

          white-space:
            nowrap;
        }


        .am-brand-detail-table
        tbody
        td {
          padding:
            10px
            12px !important;

          vertical-align:
            middle;

          color:
            rgba(
              255,
              255,
              255,
              0.72
            ) !important;

          border:
            0 !important;

          border-bottom:
            1px solid
            rgba(
              255,
              255,
              255,
              0.045
            ) !important;
        }


        .am-brand-detail-table
        tbody
        tr:hover
        td {
          background:
            rgba(
              255,
              255,
              255,
              0.035
            ) !important;
        }


        .am-brand-client-name {
          max-width:
            290px;

          overflow:
            hidden;

          color:
            rgba(
              255,
              255,
              255,
              0.92
            );

          font-weight:
            750;

          text-overflow:
            ellipsis;

          white-space:
            nowrap;
        }


        .am-brand-client-id {
          display:
            block;

          margin-top:
            2px;

          color:
            rgba(
              255,
              255,
              255,
              0.32
            );

          font-size:
            0.58rem;
        }


        .am-brand-origin-badge {
          display:
            inline-flex !important;

          align-items:
            center;

          justify-content:
            center;

          min-width:
            42px;

          padding:
            4px
            8px !important;

          color:
            #ff9a60 !important;

          background:
            linear-gradient(
              135deg,
              rgba(
                241,
                111,
                36,
                0.20
              ),
              rgba(
                241,
                111,
                36,
                0.08
              )
            ) !important;

          border:
            1px solid
            rgba(
              241,
              111,
              36,
              0.36
            ) !important;

          font-size:
            0.58rem !important;

          font-weight:
            850 !important;
        }


        .am-brand-detail-value {
          color:
            #ff8847;

          white-space:
            nowrap;
        }


        .am-brand-detail-table
        tfoot
        td {
          padding:
            12px
            13px !important;

          color:
            #fff !important;

          background:
            rgba(
              241,
              111,
              36,
              0.10
            ) !important;

          border:
            0 !important;

          font-size:
            0.78rem;

          font-weight:
            850;
        }


        .am-brand-detail-table
        tfoot
        td:last-child {
          color:
            #ff8847 !important;
        }


        /* ======================================================
           EMPTY
        ====================================================== */

        .am-brand-empty {
          min-height:
            220px;

          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          justify-content:
            center;

          padding:
            30px;

          text-align:
            center;

          color:
            rgba(
              255,
              255,
              255,
              0.40
            );
        }


        .am-brand-empty svg {
          margin-bottom:
            12px;

          color:
            #f16f24;
        }


        .am-brand-empty strong {
          color:
            rgba(
              255,
              255,
              255,
              0.85
            );

          margin-bottom:
            4px;
        }


        .am-brand-empty span {
          font-size:
            0.72rem;
        }


        /* ======================================================
           MOBILE
        ====================================================== */

        @media screen and (max-width: 767px) {

          .am-brand-modal-dialog {
            width:
              calc(
                100vw - 14px
              ) !important;

            max-width:
              none !important;

            margin:
              7px auto !important;
          }


          .am-brand-modal-content {
            max-height:
              calc(
                100dvh - 14px
              );

            border-radius:
              19px !important;
          }


          .am-brand-modal-header {
            padding:
              16px
              15px
              10px !important;
          }


          .am-brand-modal-body {
            padding:
              8px
              15px
              15px !important;
          }


          .am-brand-modal-icon {
            flex-basis:
              38px;

            width:
              38px;

            height:
              38px;

            border-radius:
              12px;
          }


          .am-brand-modal-name {
            font-size:
              0.95rem;
          }


          .am-brand-modal-kpis {
            grid-template-columns:
              1fr 1fr;
          }


          .am-brand-modal-kpis
          .am-brand-kpi:first-child {
            grid-column:
              1 / -1;
          }


          .am-brand-location-column,
          .am-brand-origin-column {
            display:
              none;
          }


          .am-brand-detail-table
          thead
          th,
          .am-brand-detail-table
          tbody
          td {
            padding:
              9px
              8px !important;
          }


          .am-brand-client-name {
            max-width:
              160px;

            font-size:
              0.70rem;
          }


          .am-brand-detail-value {
            font-size:
              0.70rem;
          }

        }

      `}</style>
    </>
  );
};


export default SalesChartBrand;